// src/app/services/firestore.service.ts
import { Injectable, inject, EnvironmentInjector, runInInjectionContext } from '@angular/core';
import {
  Firestore,
  doc,
  getDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  onSnapshot,
  collection,
  getDocs,
  DocumentData,
  DocumentReference,
  addDoc,
  startAfter,
  limit,
  collectionGroup,
  Query
} from '@angular/fire/firestore';
import { Observable } from 'rxjs';
import { increment, orderBy, query, QueryConstraint, QueryDocumentSnapshot, Unsubscribe } from 'firebase/firestore';

@Injectable({ providedIn: 'root' })
export class FirestoreService {
  public  afs = inject(Firestore);
  private injector = inject(EnvironmentInjector);

  async getDocById<T extends DocumentData>(path: string, id: string): Promise<T | null> {
    const ref = doc(this.afs, path, id);
    try {
      const snap = await runInInjectionContext(this.injector, () => getDoc(ref));
      return snap.exists() ? (snap.data() as T) : null;
    } catch (error) {
      console.error(`[FirestoreService] Error en getDocById -> ${path}/${id}`, error);
      throw error;
    }
  }

  async setDoc<T extends DocumentData>(path: string, id: string, data: T): Promise<void> {
    const ref = doc(this.afs, path, id);
    try {
      await runInInjectionContext(this.injector, () => setDoc(ref, data));
    } catch (error) {
      console.error(`[FirestoreService] Error en setDoc -> ${path}/${id}`, error);
      throw error;
    }
  }

  async updateDoc<T extends DocumentData>(path: string, id: string, data: Partial<T>): Promise<void> {
    const ref = doc(this.afs, path, id) as DocumentReference<T>;
    try {
      await runInInjectionContext(this.injector, () => updateDoc(ref, data as T));
    } catch (error) {
      console.error(`[FirestoreService] Error en updateDoc -> ${path}/${id}`, error);
      throw error;
    }
  }

  async deleteDoc(path: string, id: string): Promise<void> {
    const ref = doc(this.afs, path, id);
    try {
      await runInInjectionContext(this.injector, () => deleteDoc(ref));
    } catch (error) {
      console.error(`[FirestoreService] Error en deleteDoc -> ${path}/${id}`, error);
      throw error;
    }
  }

  listenDoc<T extends DocumentData>(path: string, id: string, callback: (data: T | null) => void): Unsubscribe {
    const ref = doc(this.afs, path, id);
    return onSnapshot(
      ref,
      snap => {
        if (snap.exists()) {
          callback({ ...(snap.data() as T), uid: snap.id });
        } else {
          console.warn(`[FirestoreService] Documento no encontrado: ${path}/${id}`);
          callback(null);
        }
      },
      error => {
        console.error(`[FirestoreService] Error en listenDoc -> ${path}/${id}`, error);
        callback(null);
      }
    );
  }

  listenDoc$<T extends DocumentData>(path: string, id: string): Observable<T | null> {
    const ref = doc(this.afs, path, id);
    return new Observable<T | null>(subscriber => {
      const unsub = onSnapshot(
        ref,
        snap => {
          if (snap.exists()) {
            subscriber.next({ ...(snap.data() as T), uid: snap.id });
          } else {
            console.warn(`[FirestoreService] Documento no encontrado en observable: ${path}/${id}`);
            subscriber.next(null);
          }
        },
        error => {
          console.error(`[FirestoreService] Error en listenDoc$ -> ${path}/${id}`, error);
          subscriber.error(error);
        }
      );
      return () => unsub();
    });
  }

  listenCollection<T extends DocumentData>(path: string, callback: (data: (T & { uid: string })[]) => void): Unsubscribe {
    const ref = collection(this.afs, path);
    return onSnapshot(
      ref,
      snapshot => {
        const data = snapshot.docs.map(doc => ({ ...(doc.data() as T), uid: doc.id }));
        callback(data);
      },
      error => {
        console.error(`[FirestoreService] Error en listenCollection -> ${path}`, error);
        callback([]);
      }
    );
  }

  async getCollection<T>(path: string): Promise<(T & { uid: string })[]> {
    const colRef = collection(this.afs, path);
    try {
      const snap = await runInInjectionContext(this.injector, () => getDocs(colRef));
      return snap.docs.map(d => ({ ...(d.data() as T), uid: d.id }));
    } catch (error) {
      console.error(`[FirestoreService] Error en getCollection -> ${path}`, error);
      throw error;
    }
  }

  listenCollection$<T extends DocumentData>(path: string): Observable<(T & { uid: string })[]> {
    const ref = collection(this.afs, path);
    return new Observable<(T & { uid: string })[]>(subscriber => {
      const unsub = onSnapshot(
        ref,
        snapshot => {
          const data = snapshot.docs.map(doc => ({ ...(doc.data() as T), uid: doc.id }));
          subscriber.next(data);
        },
        error => {
          console.error(`[FirestoreService] Error en listenCollection$ -> ${path}`, error);
          subscriber.error(error);
        }
      );
      return () => unsub();
    });
  }

  async getFilteredCollection<T>(path: string, constraints: QueryConstraint[]): Promise<(T & { uid: string })[]> {
    const ref = collection(this.afs, path);
    const q = query(ref, ...constraints);
    try {
      const snap = await runInInjectionContext(this.injector, () => getDocs(q));
      return snap.docs.map(d => ({ ...(d.data() as T), uid: d.id }));
    } catch (error) {
      console.error(`[FirestoreService] Error en getFilteredCollection -> ${path}`, error);
      throw error;
    }
  }

  async addDoc<T extends DocumentData>(path: string, data: T): Promise<void> {
    const colRef = collection(this.afs, path);
    try {
      await runInInjectionContext(this.injector, () => addDoc(colRef, data));
    } catch (error) {
      console.error(`[FirestoreService] Error en addDoc -> ${path}`, error);
      throw error;
    }
  }

  listenCollectionQuery<T extends DocumentData>(
    path: string,
    constraints: QueryConstraint[],
    callback: (data: (T & { docId: string })[]) => void
  ): Unsubscribe {
    const ref = collection(this.afs, path);
    const q = query(ref, ...constraints);
    return onSnapshot(
      q,
      snapshot => {
        const data = snapshot.docs.map(doc => ({ ...(doc.data() as T), docId: doc.id }));
        callback(data);
      },
      error => {
        console.error(`[FirestoreService] Error en listenCollectionQuery -> ${path}`, error);
        callback([]);
      }
    );
  }

  async incrementField(path: string, field: string, valor: number = 1): Promise<void> {
    const ref = doc(this.afs, path);
    try {
      await runInInjectionContext(this.injector, () => updateDoc(ref, {
        [field]: increment(valor)
      }));
    } catch (error) {
      console.error(`[FirestoreService] Error en incrementField -> ${path}`, error);
      throw error;
    }
  }

  async getPaginatedCollection<T extends DocumentData>(
    path: string,
    orderByField: string,
    pageSize: number,
    lastSnapshot?: QueryDocumentSnapshot<T>
  ): Promise<{
    data: (T & { uid: string })[];
    lastSnapshot: QueryDocumentSnapshot<T> | null;
  }> {
    try {
      let qRef = query(collection(this.afs, path), orderBy(orderByField), limit(pageSize));
      if (lastSnapshot) {
        qRef = query(collection(this.afs, path), orderBy(orderByField), startAfter(lastSnapshot), limit(pageSize));
      }
      const snap = await runInInjectionContext(this.injector, () => getDocs(qRef));
      const docs = snap.docs.map(d => ({ ...(d.data() as T), uid: d.id }));
      const nextCursor = snap.docs.length ? (snap.docs[snap.docs.length - 1] as QueryDocumentSnapshot<T>) : null;
      return { data: docs, lastSnapshot: nextCursor };
    } catch (error) {
      console.error(`[FirestoreService] Error en getPaginatedCollection -> ${path}`, error);
      throw error;
    }
  }

  async countCollection(path: string): Promise<number> {
    try {
      const snap = await runInInjectionContext(this.injector, () =>
        getDocs(collection(this.afs, path))
      );
      return snap.size;
    } catch (error) {
      console.error(`[FirestoreService] Error en countCollection -> ${path}`, error);
      throw error;
    }
  }

  async collectionGroupQuery<T>(
    collectionId: string,
    build: (ref: Query) => Query
  ): Promise<(T & { id: string })[]> {
    const ref = collectionGroup(this.afs, collectionId);
    const q = build(ref);
    try {
      const snap = await runInInjectionContext(this.injector, () => getDocs(q));
      return snap.docs.map(d => ({ ...(d.data() as T), id: d.id }));
    } catch (error) {
      console.error(`[FirestoreService] Error en collectionGroupQuery -> ${collectionId}`, error);
      throw error;
    }
  }
}
